<template>
    <div class="admin-approval-container">
        <!-- Header with Gradient -->
        <div class="approval-header">
            <div class="container">
                <div class="d-flex justify-content-between align-items-center flex-wrap">
                    <div>
                        <h1 class="header-title">
                            <b-icon-shield-check class="mr-2"></b-icon-shield-check>
                            Non-Inventory Item Approval
                        </h1>
                        <p class="header-subtitle">Review and manage non-inventory item requests</p>
                    </div>
                    <div v-if="requestData" class="status-indicator-wrapper">
                        <b-badge 
                            :variant="getStatusVariant(requestData.status)" 
                            pill 
                            class="status-badge-large p-3"
                        >
                            <b-icon 
                                :icon="getStatusIcon(requestData.status)"
                                class="mr-2"
                            ></b-icon>
                            {{ requestData.status ? requestData.status.toUpperCase() : 'LOADING...' }}
                        </b-badge>
                    </div>
                </div>
            </div>
        </div>

        <div class="container mt-4">
            <!-- Loading State -->
            <b-card v-if="loading" class="text-center py-5">
                <b-spinner variant="primary" label="Loading..." style="width: 3rem; height: 3rem;"></b-spinner>
                <p class="mt-3">Loading request details...</p>
            </b-card>

            <!-- Error State -->
            <b-card v-else-if="error" class="text-center py-4">
                <b-icon-exclamation-triangle variant="danger" font-scale="4" class="mb-3"></b-icon-exclamation-triangle>
                <h4 class="text-danger">Error Loading Request</h4>
                <p class="text-muted">{{ error }}</p>
                <b-button variant="primary" @click="goToDashboard" class="mt-2">
                    <b-icon-house class="mr-2"></b-icon-house>
                    Go to Dashboard
                </b-button>
            </b-card>

            <!-- Request Data -->
            <div v-else-if="requestData">
                <!-- Pending Request -->
                <div v-if="requestData.status === 'pending'">
                    <b-alert variant="info" show class="alert-info-custom">
                        <b-icon-info-circle class="mr-2"></b-icon-info-circle>
                        <strong>Action Required:</strong> Please review the request below and approve or reject it.
                    </b-alert>
                    
                    <!-- Request Details Card -->
                    <b-card class="request-details-card shadow-sm">
                        <div class="card-header-custom">
                            <h5 class="mb-0">
                                <b-icon-file-text class="mr-2"></b-icon-file-text>
                                Request Details
                            </h5>
                            <b-badge variant="warning" pill>
                                <b-icon-clock class="mr-1"></b-icon-clock>
                                Pending
                            </b-badge>
                        </div>
                        
                        <b-card-body>
                            <!-- Show Image if available -->
                            <div v-if="requestData.image_url" class="mb-4 text-center">
                                <img 
                                    :src="requestData.image_url" 
                                    style="max-width: 200px; max-height: 200px; border-radius: 8px; border: 1px solid #ddd; padding: 5px;"
                                    alt="Product Image"
                                />
                            </div>
                            
                            <div class="request-grid">
                                <!-- Left Column -->
                                <div class="request-info">
                                    <div class="info-item">
                                        <span class="info-label">Request ID</span>
                                        <span class="info-value"><strong>#{{ requestData.id }}</strong></span>
                                    </div>
                                    <div class="info-item">
                                        <span class="info-label">SKU</span>
                                        <span class="info-value"><b-badge variant="info" pill>{{ requestData.product_sku }}</b-badge></span>
                                    </div>
                                    <div class="info-item">
                                        <span class="info-label">Vendor</span>
                                        <span class="info-value">
                                            {{ getVendorName(requestData.vendor) }}
                                        </span>
                                    </div>
                                    <div class="info-item">
                                        <span class="info-label">Quantity</span>
                                        <span class="info-value">{{ requestData.quantity }}</span>
                                    </div>
                                    <div class="info-item">
                                        <span class="info-label">Price</span>
                                        <span class="info-value price-highlight">${{ parseFloat(requestData.price || requestData.selling_price).toFixed(2) }}</span>
                                    </div>
                                </div>
                                
                                <!-- Right Column -->
                                <div class="request-info">
                                    <div class="info-item">
                                        <span class="info-label">Invoice</span>
                                        <span class="info-value"><b-badge variant="secondary">{{ requestData.invoice_id }}</b-badge></span>
                                    </div>
                                    <div class="info-item">
                                        <span class="info-label">Requested By</span>
                                        <span class="info-value">
                                            <b-icon-person class="mr-1"></b-icon-person>
                                            {{ requestData.requested_by }}
                                        </span>
                                    </div>
                                    <div class="info-item">
                                        <span class="info-label">Customer Email</span>
                                        <span class="info-value">{{ requestData.customer_email || 'N/A' }}</span>
                                    </div>
                                    <div class="info-item">
                                        <span class="info-label">Request Date</span>
                                        <span class="info-value">
                                            <b-icon-calendar class="mr-1"></b-icon-calendar>
                                            {{ formatDate(requestData.request_date) }}
                                        </span>
                                    </div>
                                    <div class="info-item" v-if="requestData.notes">
                                        <span class="info-label">Notes</span>
                                        <span class="info-value">{{ requestData.notes }}</span>
                                    </div>
                                </div>
                            </div>
                        </b-card-body>
                        
                        <!-- Approval Actions -->
                        <div class="card-footer-actions">
                            <div class="d-flex justify-content-between align-items-center flex-wrap">
                                <div class="text-muted small">
                                    <b-icon-clock-history class="mr-1"></b-icon-clock-history>
                                    Requested by: <strong>{{ requestData.requested_by }}</strong>
                                    {{ requestData.request_date ? 'on ' + formatDate(requestData.request_date) : '' }}
                                </div>
                                
                                <div class="action-buttons">
                                    <b-button 
                                        variant="outline-secondary"
                                        class="mr-2"
                                        @click="goToDashboard"
                                    >
                                        <b-icon-arrow-left class="mr-1"></b-icon-arrow-left>
                                        Back
                                    </b-button>
                                    <b-button 
                                        variant="danger" 
                                        @click="showRejectModal = true"
                                        :disabled="processing"
                                        class="btn-reject"
                                    >
                                        <b-icon-x-circle class="mr-1"></b-icon-x-circle>
                                        Reject
                                    </b-button>
                                    <b-button 
                                        variant="success" 
                                        @click="processApproval('approve')"
                                        :disabled="processing"
                                        class="btn-approve"
                                    >
                                        <b-spinner small v-if="processing"></b-spinner>
                                        <b-icon-check-circle v-else class="mr-1"></b-icon-check-circle>
                                        {{ processing ? 'Processing...' : 'Approve' }}
                                    </b-button>
                                </div>
                            </div>
                        </div>
                    </b-card>
                </div>
                
                <!-- Already Processed -->
                <div v-else>
                    <b-alert 
                        :variant="requestData.status === 'approved' ? 'success' : 'danger'" 
                        show
                        class="alert-processed"
                    >
                        <div class="d-flex align-items-center">
                            <b-icon 
                                :icon="requestData.status === 'approved' ? 'check-circle-fill' : 'x-circle-fill'"
                                font-scale="2"
                                class="mr-3"
                            ></b-icon>
                            <div>
                                <h5 class="mb-1">Request {{ requestData.status ? requestData.status.toUpperCase() : 'PROCESSED' }}</h5>
                                <p class="mb-0">
                                    <span v-if="requestData.status === 'approved'">
                                        This request has been approved by <strong>{{ requestData.approved_by || 'System' }}</strong>.
                                        The item has been added to the sale.
                                    </span>
                                    <span v-else-if="requestData.status === 'rejected'">
                                        This request has been rejected by <strong>{{ requestData.approved_by || 'System' }}</strong>.
                                        <span v-if="requestData.rejection_reason">
                                            <br><strong>Reason:</strong> {{ requestData.rejection_reason }}
                                        </span>
                                    </span>
                                </p>
                            </div>
                        </div>
                    </b-alert>
                    
                    <!-- Show request details even if processed -->
                    <b-card class="request-details-card shadow-sm">
                        <div class="card-header-custom">
                            <h5 class="mb-0">
                                <b-icon-file-text class="mr-2"></b-icon-file-text>
                                Request Details
                            </h5>
                            <b-badge 
                                :variant="requestData.status === 'approved' ? 'success' : 'danger'" 
                                pill
                            >
                                {{ requestData.status.toUpperCase() }}
                            </b-badge>
                        </div>
                        
                        <b-card-body>
                            <!-- Show Image if available -->
                            <div v-if="requestData.image_url" class="mb-4 text-center">
                                <img 
                                    :src="requestData.image_url" 
                                    style="max-width: 200px; max-height: 200px; border-radius: 8px; border: 1px solid #ddd; padding: 5px;"
                                    alt="Product Image"
                                />
                            </div>
                            
                            <div class="request-grid">
                                <div class="request-info">
                                    <div class="info-item">
                                        <span class="info-label">Request ID</span>
                                        <span class="info-value"><strong>#{{ requestData.id }}</strong></span>
                                    </div>
                                    <div class="info-item">
                                        <span class="info-label">SKU</span>
                                        <span class="info-value"><b-badge variant="info" pill>{{ requestData.product_sku }}</b-badge></span>
                                    </div>
                                    <div class="info-item">
                                        <span class="info-label">Vendor</span>
                                        <span class="info-value">
                                            {{ getVendorName(requestData.vendor) }}
                                        </span>
                                    </div>
                                    <div class="info-item">
                                        <span class="info-label">Quantity</span>
                                        <span class="info-value">{{ requestData.quantity }}</span>
                                    </div>
                                    <div class="info-item">
                                        <span class="info-label">Price</span>
                                        <span class="info-value price-highlight">${{ parseFloat(requestData.price || requestData.selling_price).toFixed(2) }}</span>
                                    </div>
                                </div>
                                <div class="request-info">
                                    <div class="info-item">
                                        <span class="info-label">Invoice</span>
                                        <span class="info-value"><b-badge variant="secondary">{{ requestData.invoice_id }}</b-badge></span>
                                    </div>
                                    <div class="info-item">
                                        <span class="info-label">Requested By</span>
                                        <span class="info-value">{{ requestData.requested_by }}</span>
                                    </div>
                                    <div class="info-item">
                                        <span class="info-label">Request Date</span>
                                        <span class="info-value">{{ formatDate(requestData.request_date) }}</span>
                                    </div>
                                    <div class="info-item" v-if="requestData.approved_by">
                                        <span class="info-label">Processed By</span>
                                        <span class="info-value">{{ requestData.approved_by }}</span>
                                    </div>
                                    <div class="info-item" v-if="requestData.approved_date">
                                        <span class="info-label">Processed Date</span>
                                        <span class="info-value">{{ formatDate(requestData.approved_date) }}</span>
                                    </div>
                                    <div class="info-item" v-if="requestData.rejection_reason">
                                        <span class="info-label">Rejection Reason</span>
                                        <span class="info-value text-danger">{{ requestData.rejection_reason }}</span>
                                    </div>
                                </div>
                            </div>
                        </b-card-body>
                        
                        <div class="card-footer-actions">
                            <b-button variant="secondary" @click="goToDashboard">
                                <b-icon-house class="mr-1"></b-icon-house>
                                Go to Dashboard
                            </b-button>
                        </div>
                    </b-card>
                </div>
            </div>
        </div>
        
        <!-- Reject Modal -->
        <b-modal 
            id="reject-modal"
            v-model="showRejectModal"
            title="Reject Request"
            size="md"
            centered
            hide-footer
            header-class="modal-header-danger"
        >
            <div class="modal-body-custom">
                <b-icon-exclamation-triangle variant="danger" font-scale="3" class="mb-3 d-block text-center"></b-icon-exclamation-triangle>
                <h5 class="text-center mb-3">Confirm Rejection</h5>
                <p class="text-center text-muted">Please provide a reason for rejecting this request:</p>
                
                <b-form @submit.prevent="processApproval('reject')">
                    <b-form-group label="Rejection Reason">
                        <b-form-textarea
                            v-model="rejectionReason"
                            placeholder="Enter reason for rejection..."
                            rows="4"
                            required
                            class="border-danger"
                            :state="rejectionReason ? 'valid' : null"
                        ></b-form-textarea>
                        <b-form-invalid-feedback>
                            Please provide a reason for rejection.
                        </b-form-invalid-feedback>
                    </b-form-group>
                    
                    <div class="d-flex justify-content-end gap-2 mt-4">
                        <b-button 
                            variant="secondary" 
                            @click="showRejectModal = false"
                            :disabled="processing"
                        >
                            Cancel
                        </b-button>
                        <b-button 
                            variant="danger" 
                            type="submit"
                            :disabled="!rejectionReason || processing"
                            class="px-4"
                        >
                            <b-spinner small v-if="processing"></b-spinner>
                            <b-icon-x-circle v-else class="mr-1"></b-icon-x-circle>
                            {{ processing ? 'Processing...' : 'Confirm Rejection' }}
                        </b-button>
                    </div>
                </b-form>
            </div>
        </b-modal>
    </div>
</template>

<script>
import Requestor from "@/utilities/Requestor";

export default {
    name: 'AdminApproval',
    data() {
        return {
            requestData: null,
            loading: true,
            error: null,
            processing: false,
            showRejectModal: false,
            rejectionReason: '',
            // Add vendors from localStorage
            vendors: []
        };
    },
    
    created() {
        // Load vendors from localStorage
        try {
            const packet = localStorage.getItem('packet');
            if (packet) {
                const parsedPacket = JSON.parse(packet);
                this.vendors = parsedPacket.vendors || [];
                console.log('Vendors loaded:', this.vendors);
            }
        } catch (e) {
            console.error('Error loading vendors:', e);
        }
        
        const requestId = this.$route.query.request_id;
        const token = this.$route.query.token;
        const action = this.$route.query.action;
        
        console.log('AdminApproval created:', { requestId, token, action });
        
        if (requestId && token) {
            this.fetchRequest(requestId, token);
        } else {
            this.error = 'Invalid approval link. Missing request ID or token.';
            this.loading = false;
        }
    },
    
    methods: {
        fetchRequest(requestId, token) {
            this.loading = true;
            this.error = null;
            
            const path = "/api/sales/check_non_inventory_request_status/";
            const data = { request_id: requestId };
            
            const success = (response) => {
                console.log('Fetch request response:', response);
                if (response.data.error) {
                    this.error = response.data.error;
                } else if (response.data.result) {
                    this.requestData = response.data.result;
                    
                    if (this.requestData.request_token !== token) {
                        this.error = 'Invalid token. This link may have expired or been tampered with.';
                    }
                } else {
                    this.error = 'Request not found';
                }
                this.loading = false;
            };
            
            const failure = (error) => {
                console.error("Error fetching request:", error);
                this.error = 'Failed to load request. Please try again.';
                this.loading = false;
            };
            
            Requestor.getSyncP(path, data, this, success, failure);
        },
        
        getVendorName(vendorId) {
            if (!vendorId) return 'N/A';
            
            // If vendorId is already a string (vendor name), return it
            if (typeof vendorId === 'string' && isNaN(vendorId) && !vendorId.match(/^\d+$/)) {
                return vendorId;
            }
            
            // Try to find vendor in the vendors array
            if (this.vendors && Array.isArray(this.vendors)) {
                // Convert vendorId to number for comparison
                const vendorIdNum = parseInt(vendorId);
                
                const vendor = this.vendors.find(v => {
                    if (typeof v === 'object') {
                        // Check if vendor has value or id property
                        const vValue = v.value !== undefined ? parseInt(v.value) : null;
                        const vId = v.id !== undefined ? parseInt(v.id) : null;
                        const vText = v.text || v.name || v.label || '';
                        
                        // Match by value, id, or text
                        return vValue === vendorIdNum || 
                               vId === vendorIdNum || 
                               vText === vendorId || 
                               vText === vendorIdNum;
                    }
                    return v == vendorId || v == vendorIdNum;
                });
                
                if (vendor) {
                    // Return the text/name from the vendor object
                    if (typeof vendor === 'object') {
                        return vendor.text || vendor.name || vendor.label || vendor.value || vendorId;
                    }
                    return vendor;
                }
            }
            
            // If vendor not found, return the ID
            return vendorId;
        },
        
        processApproval(action) {
            if (action === 'reject' && !this.rejectionReason) {
                this.$bvToast.toast(
                    "Please provide a reason for rejection",
                    {title: "Required", variant: "warning"}
                );
                return;
            }
            
            this.processing = true;
            
            let approvedBy = 'Admin';
            try {
                if (this.$store && this.$store.state && this.$store.state.user) {
                    approvedBy = this.$store.state.user.username || 'Admin';
                }
            } catch (e) {
                console.warn('Could not get username from store:', e);
            }
            
            if (approvedBy === 'Admin') {
                try {
                    const userDetails = localStorage.getItem('userDetails');
                    if (userDetails) {
                        const parsed = JSON.parse(userDetails);
                        if (parsed && parsed.username) {
                            approvedBy = parsed.username;
                        }
                    }
                } catch (e) {
                    console.warn('Could not get username from localStorage:', e);
                }
            }
            
            const path = "/api/sales/approve_non_inventory_request/";
            const data = {
                request_id: this.requestData.id,
                action: action,
                approved_by: approvedBy,
                rejection_reason: this.rejectionReason,
                invoice_id: this.requestData.invoice_id
            };
            
            console.log('Processing approval:', data);
            
            const success = (response) => {
                console.log('Approval response:', response);
                if (response.data.error) {
                    this.$bvToast.toast(
                        response.data.error,
                        {title: "Error", variant: "danger"}
                    );
                } else {
                    this.fetchRequest(this.requestData.id, this.$route.query.token);
                    
                    this.$bvToast.toast(
                        `Request ${action === 'approve' ? 'approved' : 'rejected'} successfully!`,
                        {title: "Success", variant: action === 'approve' ? 'success' : 'warning'}
                    );
                    
                    this.showRejectModal = false;
                    this.rejectionReason = '';
                }
                this.processing = false;
            };
            
            const failure = (error) => {
                console.error("Error processing request:", error);
                this.$bvToast.toast(
                    "Failed to process request. Please try again.",
                    {title: "Error", variant: "danger"}
                );
                this.processing = false;
            };
            
            Requestor.postSyncP(path, data, this, success, failure);
        },
        
        getStatusVariant(status) {
            const variants = {
                'pending': 'warning',
                'approved': 'success',
                'rejected': 'danger'
            };
            return variants[status] || 'secondary';
        },
        
        getStatusIcon(status) {
            const icons = {
                'pending': 'clock',
                'approved': 'check-circle',
                'rejected': 'x-circle'
            };
            return icons[status] || 'question-circle';
        },
        
        formatDate(date) {
            if (!date) return 'N/A';
            try {
                return new Date(date).toLocaleString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                });
            } catch (error) {
                return date;
            }
        },
        
        goToDashboard() {
            this.$router.push('/home/');
        }
    }
};
</script>

<style scoped>
.admin-approval-container {
    min-height: 100vh;
    background: #f5f7fa;
}

/* Header Styles */
.approval-header {
    background: linear-gradient(135deg, #2c3e50 0%, #4a90e2 100%);
    padding: 30px 0;
    color: white;
    box-shadow: 0 4px 15px rgba(0,0,0,0.1);
}

.header-title {
    font-weight: 700;
    font-size: 28px;
    margin-bottom: 5px;
}

.header-subtitle {
    opacity: 0.8;
    font-size: 14px;
    margin-bottom: 0;
}

.status-indicator-wrapper {
    background: rgba(255,255,255,0.15);
    border-radius: 50px;
    padding: 5px;
}

.status-badge-large {
    font-size: 16px;
    font-weight: 600;
    letter-spacing: 0.5px;
}

/* Alert Styles */
.alert-info-custom {
    border-radius: 12px;
    border-left: 4px solid #17a2b8;
    background: #e8f4fd;
}

.alert-processed {
    border-radius: 12px;
    border-left: 4px solid;
    padding: 20px;
}

/* Card Styles */
.request-details-card {
    border-radius: 12px !important;
    overflow: hidden;
    border: none;
}

.card-header-custom {
    background: #f8f9fa;
    padding: 15px 20px;
    border-bottom: 2px solid #e9ecef;
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.card-footer-actions {
    background: #f8f9fa;
    padding: 15px 20px;
    border-top: 1px solid #e9ecef;
}

/* Request Grid */
.request-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 20px;
}

.request-info {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.info-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 0;
    border-bottom: 1px solid #f0f0f0;
}

.info-item:last-child {
    border-bottom: none;
}

.info-label {
    font-size: 13px;
    color: #6c757d;
    font-weight: 500;
}

.info-value {
    font-size: 14px;
    color: #2d3748;
}

.price-highlight {
    font-size: 20px;
    font-weight: 700;
    color: #2c5aa0;
}

/* Action Buttons */
.action-buttons {
    display: flex;
    gap: 10px;
}

.btn-approve {
    border-radius: 25px;
    padding: 8px 25px;
    font-weight: 600;
    transition: all 0.3s ease;
    background: linear-gradient(135deg, #28a745 0%, #20c997 100%);
    border: none;
}

.btn-approve:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 4px 15px rgba(40, 167, 69, 0.3);
}

.btn-reject {
    border-radius: 25px;
    padding: 8px 20px;
    font-weight: 600;
    transition: all 0.3s ease;
}

.btn-reject:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 4px 15px rgba(220, 53, 69, 0.3);
}

/* Modal Styles */
.modal-header-danger {
    background: #dc3545;
    color: white;
}

.modal-body-custom {
    padding: 20px;
}

.border-danger:focus {
    border-color: #dc3545;
    box-shadow: 0 0 0 0.2rem rgba(220, 53, 69, 0.25);
}

/* Responsive */
@media (max-width: 768px) {
    .approval-header {
        padding: 20px 0;
    }
    
    .header-title {
        font-size: 22px;
    }
    
    .header-subtitle {
        font-size: 12px;
    }
    
    .request-grid {
        grid-template-columns: 1fr;
        gap: 10px;
    }
    
    .info-item {
        flex-direction: column;
        align-items: flex-start;
        gap: 3px;
    }
    
    .action-buttons {
        flex-direction: column;
        width: 100%;
        margin-top: 10px;
    }
    
    .action-buttons .btn {
        width: 100%;
        justify-content: center;
    }
    
    .card-footer-actions .d-flex {
        flex-direction: column;
        align-items: stretch !important;
    }
    
    .card-footer-actions .text-muted {
        text-align: center;
        margin-bottom: 10px;
    }
    
    .status-badge-large {
        font-size: 13px;
        padding: 8px 15px !important;
    }
    
    .price-highlight {
        font-size: 17px;
    }
}

@media (max-width: 576px) {
    .container {
        padding-left: 10px;
        padding-right: 10px;
    }
    
    .header-title {
        font-size: 18px;
    }
}
</style>


// WEBPACK FOOTER //
// src/components/Sales/AdminApproval.vue